import { useMutation } from '@apollo/client'
import { Box, Button, Code, Divider, Flex, FormModal, Link, Text, VStack } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { CopyableText } from '../../../_shared/components'
import { Toast } from '../../../_shared/components/toast'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_MCLEOD_APP, REQUEST_TMS_LOAD_SYNC, UPDATE_MCLEOD_APP } from '../data/mutations'

type TFieldValues = {
  companyID: string
  mcLeodInstanceURL: string
  mcLeodServerTimezone: { label: string; value: string }
  mcLeodVersion: string
  password: string
  username: string
}

const validationSchema = Yup.object().shape({
  companyID: Yup.string().required('Company ID is required'),
  mcLeodInstanceURL: Yup.string().required('McLeod instance URL is required'),
  mcLeodVersion: Yup.string()
    .required('McLeod Version is required')
    .matches(
      /^(0[0-9]|[1-9][0-9])\.[12]$/,
      'Version must be in format YY.1 (spring) or YY.2 (fall), e.g. 24.1 or 24.2'
    ),
  password: Yup.string().required('Password is required'),
  username: Yup.string().required('Username is required')
})

/**
 * This displays the external workspace ID (called accountID in McLeod) after the McLeod app is enabled
 */
const ExternalWorkspaceIDDisplay = ({
  isOpen,
  isEnabled,
  externalWorkspaceID
}: {
  externalWorkspaceID?: string | null
  isEnabled: boolean
  isOpen: boolean
}) => {
  if (!isOpen || !isEnabled || !externalWorkspaceID) {
    return null
  }

  return (
    <Box>
      <Flex justifyItems="center" flexDirection="column">
        {/* <AlertTitle className="text-green-800 font-medium">McLeod Account ID</AlertTitle> */}
        <Text>Copy the ID below and add it in the Freight Matching Control</Text>
        <Box marginTop={2} marginBottom={2}>
          <Text>McLeod Account ID:</Text>
          <Code width="100%" marginTop={2} padding={2} cursor="pointer">
            <CopyableText value={externalWorkspaceID}>
              <code className="font-mono text-sm">{externalWorkspaceID}</code>
            </CopyableText>
          </Code>
        </Box>
      </Flex>
    </Box>
  )
}

/**
 * McLeod Modal component that handles collecting and submitting McLeod credentials
 * The external workspace ID (Account ID in McLeod) is now generated during enablement
 */
const McLeodModal = React.memo((props: AppModalProps) => {
  const OFFSET_MAPPER: Record<string, { label: string; value: string }> = {
    '-0400': {
      label: 'Eastern Time',
      value: 'ET'
    },
    '-0500': {
      label: 'Central Time',
      value: 'CT'
    },
    '-0600': {
      label: 'Mountain Time',
      value: 'MT'
    },
    '-0700': {
      label: 'Pacific Time',
      value: 'PT'
    }
  }

  const { isOpen, refetchApps, onClose, app, setModalEditable, isModalEditable } = props
  const [formError, setFormError] = useState('')
  const { showToast } = Toast()
  const [externalWorkspaceID, setExternalWorkspaceID] = useState<string | null>(
    app.workspaceApp?.appCredentials?.chaineAPIToken || null
  )

  // McLeod app enable mutation
  const [enableMcleodApp, { loading: isEnabling }] = useMutation(ENABLE_MCLEOD_APP, {
    onCompleted: (result) => {
      setExternalWorkspaceID(result.enableMcLeodApp.externalWorkspaceID)
      showToast({
        status: 'success',
        title: 'McLeod app enabled successfully!'
      })
      refetchApps()
      setFormError('')
    },
    onError: (err) => {
      setFormError(`${err.message}`)
      showToast({
        status: 'error',
        title: `${err.message}`
      })
    }
  })

  const [updateMcLeodApp, { loading: isUpdating }] = useMutation(UPDATE_MCLEOD_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: 'McLeod app enabled successfully!'
      })
      refetchApps()
      setFormError('')
    },
    onError: (err) => {
      setFormError(`${err.message}`)
      showToast({
        status: 'error',
        title: `${err.message}`
      })
    }
  })

  // Add the sync mutation
  const [requestTMSLoadSync, { loading: isSyncing }] = useMutation(REQUEST_TMS_LOAD_SYNC, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: 'Load sync requested successfully'
      })
    },
    onError: (err) => {
      showToast({
        status: 'error',
        title: `Failed to request load sync: ${err.message}`
      })
    }
  })

  const handleCredentialsSubmit: SubmitHandler<TFieldValues> = (values) => {
    enableMcleodApp({
      variables: {
        companyID: values.companyID,
        mcLeodInstanceURL: values.mcLeodInstanceURL,
        mcLeodServerTimezone: values.mcLeodServerTimezone.value,
        mcLeodVersion: parseFloat(values.mcLeodVersion),
        password: values.password,
        username: values.username // Convert to number for the mutation
      }
    })
  }

  const handleUpdate: SubmitHandler<TFieldValues> = (values) => {
    updateMcLeodApp({
      variables: {
        companyID: values.companyID,
        mcLeodInstanceURL: values.mcLeodInstanceURL,
        mcLeodServerTimezone: values.mcLeodServerTimezone.value,
        mcLeodVersion: parseFloat(values.mcLeodVersion),
        password: values.password,
        username: values.username // Convert to number for the mutation
      }
    })
  }

  const handleSyncRequest = () => {
    requestTMSLoadSync({
      variables: {
        appType: 'McLeod'
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={closeModal}
      size="lg"
      title="Enable McLeod for Chaine"
      subtitle="Enter the McLeod TMS credentials created for the Chaine User. Refer to the integration guide for more information."
      formSubmitHandler={isModalEditable ? handleUpdate : handleCredentialsSubmit}
      buttonText={app?.workspaceApp?.appCredentials ? 'Update' : 'Enable'}
      validationSchema={validationSchema}
      isLoading={isEnabling || isUpdating}
      footer={
        <VStack>
          {formError && (
            <Box mb={4}>
              <Text color="red.600">{formError}</Text>
            </Box>
          )}
          <VStack spacing={2} align="stretch">
            <Divider />
            <ExternalWorkspaceIDDisplay
              isOpen={isOpen}
              isEnabled={!!app?.workspaceApp?.appCredentials}
              externalWorkspaceID={app?.workspaceApp?.externalWorkspaceID || externalWorkspaceID}
            />
            <Divider />
            {app?.workspaceApp?.appCredentials && (
              <Flex flexDirection="column" gap={4} justifyContent="center" alignItems="center" width="100%">
                <Text textAlign="justify" whiteSpace="pre-wrap">
                  Fetch all active loads from McLeod and add them to Chain. This is helpful if you had downtime and need
                  to resync loads. Note: can only be run once per day
                </Text>
                <Button
                  colorScheme="blue"
                  onClick={handleSyncRequest}
                  isLoading={isSyncing}
                  loadingText="Requesting sync..."
                >
                  Sync Active Loads
                </Button>
              </Flex>
            )}
          </VStack>
        </VStack>
      }
      initialValues={
        app?.workspaceApp?.appCredentials
          ? {
              companyID: app?.workspaceApp?.appMetadata?.companyID || '',
              mcLeodInstanceURL: app?.workspaceApp?.appMetadata?.mcLeodInstanceURL || '',
              mcLeodServerTimezone: OFFSET_MAPPER[app?.workspaceApp?.appMetadata?.mcLeodServerTimezone || ''],
              mcLeodVersion: app?.workspaceApp?.appMetadata?.mcLeodVersion?.toString() || '',
              password: app?.workspaceApp?.appCredentials?.password || '',
              username: app?.workspaceApp?.appCredentials?.username || ''
            }
          : undefined
      }
      modalBodyProps={{ maxHeight: 600, overflow: 'auto' }}
      modalItems={[
        {
          isRequired: true,
          labelText: 'McLeod Instance URL (e.g. https://tms-XXXXX.mcleodhosted.com )',
          name: 'mcLeodInstanceURL',
          placeHolder: 'Enter Your McLeod TMS instance URL',
          type: 'input'
        },
        {
          isRequired: true,
          labelText: 'Company ID (such as TMS, TMS2, etc.)',
          name: 'companyID',
          placeHolder: 'Enter McLeod company ID',
          type: 'input'
        },
        {
          isRequired: true,
          labelText: 'McLeod Version (YY.1 for spring or YY.2 for fall)',
          name: 'mcLeodVersion',
          placeHolder: 'Enter McLeod version (e.g. 24.2)',
          type: 'input'
        },
        {
          isRequired: true,
          labelText: 'McLeod Server Timezone',
          name: 'mcLeodServerTimezone',
          options: [
            {
              label: 'Pacific Time',
              value: 'PT'
            },
            {
              label: 'Mountain Time',
              value: 'MT'
            },
            {
              label: 'Central Time',
              value: 'CT'
            },
            {
              label: 'Eastern Time',
              value: 'ET'
            }
          ],
          placeHolder: 'Enter McLeod Local Server Timezone',
          type: 'select'
        },
        {
          isRequired: true,
          labelText: 'Username',
          name: 'username',
          placeHolder: 'Enter McLeod TMS username',
          type: 'input'
        },
        {
          inputType: 'password',
          isRequired: true,
          labelText: 'Password',
          name: 'password',
          placeHolder: 'Enter McLeod TMS password',
          type: 'input'
        }
      ]}
    >
      <Link
        color="blue.500"
        href="https://help.trychain.com/en/articles/10693778-enabling-mcleod-tms"
        target="_blank"
        rel="noopener noreferrer"
      >
        Read the integration guide
      </Link>
    </FormModal>
  )
})

McLeodModal.displayName = 'McLeodModal'

export { McLeodModal }
